<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useChatConversationsStore, type Conversation } from '@/ai/store/chatConversations';
import { useChatStore } from '@/ai/store/chat';
import { useMyInfoStore } from '@/config/stores/my-info';
import dayjs from 'dayjs';
import { useInternational } from '@/common/locale';

/**
 * 챗봇 대화 목록 패널.
 *
 * 표시: 활성 대화 목록 (lastMessageAt DESC, top 50). 항목 = 제목 + 미리보기 2줄 + 시각 + 삭제 버튼.
 * 인터랙션:
 *   - 항목 클릭 → 해당 대화 active 전환 → chat store 가 history 다시 로드 (chat.ts getChatMessage 호출)
 *   - 삭제 버튼 → confirm + soft delete
 *
 * 사용자 격리: backend GET /conversations 가 user_id 필터 강제. front 는 userInfo.userId 동봉.
 */
const { t } = useInternational();

const convStore = useChatConversationsStore();
const { list, activeId, isLoading, panelOpen, fetchError } = storeToRefs(convStore);

const chatStore = useChatStore();
const { myInfo: userInfo } = storeToRefs(useMyInfoStore());

const userIdStr = computed(() => String(userInfo.value?.userId ?? -1));

const refresh = (): void => {
  if (userIdStr.value === '-1') return;
  convStore.fetchList(userIdStr.value);
};

/** panel open 시 최신 list fetch. */
watch(panelOpen, (open) => {
  if (open) refresh();
});

onMounted(() => {
  if (panelOpen.value) refresh();
});

const formatRelative = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = dayjs(iso);
  if (!d.isValid()) return '';
  const now = dayjs();
  const diffMin = now.diff(d, 'minute');
  if (diffMin < 1) return t('WORD.CHATBOT.CONVERSATION.JUST_NOW');
  if (diffMin < 60) return t('WORD.CHATBOT.CONVERSATION.MINUTES_AGO', { n: diffMin });
  if (now.isSame(d, 'day')) return d.format('HH:mm');
  if (now.subtract(1, 'day').isSame(d, 'day'))
    return t('WORD.CHATBOT.CONVERSATION.YESTERDAY_AT', { time: d.format('HH:mm') });
  if (now.isSame(d, 'year')) return d.format('MM/DD HH:mm');
  return d.format('YYYY-MM-DD');
};

const onSelect = async (c: Conversation): Promise<void> => {
  // 전환 진행 중 재진입 차단 — 연속 클릭/생성과 겹치면 늦은 응답이 잠금을 풀거나
  // active 를 엇갈리게 바꿀 수 있다 (3차 리뷰 P1)
  if (convStore.switching) return;
  if (c.conversationId === activeId.value) {
    convStore.closePanel();
    return;
  }
  // 진행 중 스트림을 끊고 전환 — 살려두면 늦은 conversation 통보가 active 를 이전 대화로
  // 되돌려 다음 발화가 엉뚱한 대화로 전송될 수 있다 (재리뷰 P1)
  chatStore.cancelStreaming();
  convStore.switching = true;
  try {
    convStore.setActiveId(c.conversationId);
    convStore.closePanel();
    // chat store 의 chatMessages 를 새 대화로 다시 로드
    chatStore.clearMessages();
    await chatStore.getChatMessage();
  } finally {
    convStore.switching = false;
  }
};

const onDelete = async (e: Event, c: Conversation): Promise<void> => {
  e.stopPropagation();
  if (convStore.switching) return;
  const ok = window.confirm(t('WORD.CHATBOT.CONVERSATION.DELETE_CONFIRM'));
  if (!ok) return;
  const wasActive = activeId.value === c.conversationId;
  convStore.switching = true;
  try {
    const removed = await convStore.remove(userIdStr.value, c.conversationId);
    if (!removed) {
      window.alert(t('WORD.CHATBOT.CONVERSATION.DELETE_FAIL'));
      return;
    }
    if (wasActive) {
      // 활성 대화 삭제 → 진행 중 스트림도 끊고 chat 본문 비움 (다음 발화 시 backend 가 자동 새 대화 생성)
      chatStore.cancelStreaming();
      chatStore.clearMessages();
    }
  } finally {
    convStore.switching = false;
  }
};

const previewLabel = (c: Conversation): string => {
  const sender = c.lastMessageSender === 'chatbot' ? 'chatbot' : t('WORD.CHATBOT.CONVERSATION.ME');
  const body = c.lastMessagePreview || t('WORD.CHATBOT.CONVERSATION.EMPTY_TITLE');
  return `${sender}: ${body}`;
};

const displayTitle = (c: Conversation): string => {
  return c.title && c.title.trim() ? c.title : t('WORD.CHATBOT.CONVERSATION.EMPTY_TITLE');
};
</script>

<template>
  <div
    v-if="panelOpen"
    class="conversation-list-overlay"
    role="dialog"
    :aria-label="t('WORD.CHATBOT.CONVERSATION.TITLE')"
    @click.self="convStore.closePanel()"
  >
    <div class="conversation-list">
      <header class="conversation-list__header">
        <span class="conversation-list__title">{{ t('WORD.CHATBOT.CONVERSATION.TITLE') }}</span>
        <button
          class="conversation-list__close"
          :aria-label="t('WORD.CLOSE')"
          @click="convStore.closePanel()"
        >
          ×
        </button>
      </header>

      <div
        v-if="isLoading"
        class="conversation-list__empty"
      >
        {{ t('WORD.CHATBOT.LOADING') }}
      </div>
      <div
        v-else-if="fetchError"
        class="conversation-list__empty conversation-list__empty--error"
      >
        {{ fetchError }}
        <button
          class="conversation-list__retry"
          @click="convStore.fetchList(userIdStr)"
        >
          {{ t('WORD.RETRY_ANALYSIS') }}
        </button>
      </div>
      <div
        v-else-if="list.length === 0"
        class="conversation-list__empty"
      >
        {{ t('WORD.CHATBOT.CONVERSATION.EMPTY_LIST') }}
      </div>

      <ul
        v-else
        class="conversation-list__items"
      >
        <li
          v-for="c in list"
          :key="c.conversationId"
          class="conversation-item"
          :class="{ 'conversation-item--active': c.conversationId === activeId }"
          tabindex="0"
          role="button"
          :aria-label="t('WORD.CHATBOT.CONVERSATION.ITEM_OPEN', { title: displayTitle(c) })"
          @click="onSelect(c)"
          @keydown.enter="onSelect(c)"
        >
          <div class="conversation-item__row">
            <span class="conversation-item__title">{{ displayTitle(c) }}</span>
            <button
              class="conversation-item__delete"
              :aria-label="t('WORD.CHATBOT.CONVERSATION.ITEM_DELETE')"
              :title="t('WORD.CHATBOT.CONVERSATION.ITEM_DELETE')"
              @click="onDelete($event, c)"
            >
              🗑
            </button>
          </div>
          <div class="conversation-item__preview">{{ previewLabel(c) }}</div>
          <div class="conversation-item__meta">
            <span class="conversation-item__time">{{ formatRelative(c.lastMessageAt) }}</span>
            <span class="conversation-item__count"
              >{{ c.messageCount }}{{ t('WORD.COUNT_SUFFIX') }}</span
            >
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.conversation-list-overlay {
  display: flex;
  position: absolute;
  inset: 56px 0 0; // 헤더 아래
  z-index: 10;
  align-items: stretch;
  justify-content: stretch;
  background: rgb(0, 0, 0, 0.06);
}
.conversation-list {
  display: flex;
  flex: 1;
  flex-direction: column;
  max-height: calc(100% - 16px);
  margin: 8px;
  overflow: hidden;
  border: 1px solid var(--border-color, #e0e0e0);
  border-radius: 8px;
  background: var(--background-color, #ffffff);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid var(--border-color, #e0e0e0);
    font-weight: 600;
  }

  &__close {
    padding: 4px 8px;
    cursor: pointer;
    border: none;
    background: transparent;
    font-size: 18px;
    line-height: 1;
    color: var(--text-secondary, #666666);
    &:hover {
      color: var(--text-primary, #222222);
    }
  }

  &__empty {
    padding: 24px 16px;
    font-size: 13px;
    color: var(--text-secondary, #888888);
    text-align: center;

    &--error {
      color: var(--color-danger, #d32f2f);
    }
  }

  &__retry {
    display: block;
    padding: 4px 12px;
    margin: 12px auto 0;
    cursor: pointer;
    border: 1px solid var(--border-color, #cccccc);
    border-radius: 4px;
    background: transparent;
    font-size: 12px;
    &:hover {
      background: var(--background-hover, #f3f3f3);
    }
  }

  &__items {
    flex: 1;
    padding: 4px 0;
    margin: 0;
    overflow-y: auto;
    list-style: none;
  }
}
.conversation-item {
  position: relative;
  padding: 10px 14px 8px 18px;
  cursor: pointer;
  border-bottom: 1px solid var(--border-color-soft, #f0f0f0);

  &::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 3px;
    background: transparent;
    content: '';
  }

  &--active {
    background: var(--background-active, rgb(80, 120, 240, 0.07));
    &::before {
      background: var(--color-primary, #4a7dff);
    }
    .conversation-item__title {
      font-weight: 600;
    }
  }

  &:hover {
    background: var(--background-hover, rgb(0, 0, 0, 0.04));
  }
  &:focus-visible {
    outline: 2px solid var(--color-primary, #4a7dff);
    outline-offset: -2px;
  }

  &__row {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    justify-content: space-between;
  }

  &__title {
    flex: 1;
    overflow: hidden;
    font-size: 13px;
    line-height: 1.3;
    color: var(--text-primary, #222222);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__delete {
    padding: 2px 4px;
    opacity: 0.5;
    cursor: pointer;
    border: none;
    background: transparent;
    font-size: 12px;
    &:hover {
      opacity: 1;
      color: var(--color-danger, #d32f2f);
    }
  }

  &__preview {
    display: -webkit-box;
    margin-top: 4px;
    overflow: hidden;
    font-size: 12px;
    line-height: 1.3;
    color: var(--text-secondary, #777777);
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &__meta {
    display: flex;
    gap: 8px;
    margin-top: 4px;
    font-size: 11px;
    color: var(--text-tertiary, #999999);
  }
}
</style>
